import type { IntegrationVendor } from './Integrations';
import type { Tour, TourFrame } from './Tour';
import type { BasicUser, IUser } from './User';

export enum ShareSecurityLevel {
  internal = 'internal',
  restricted = 'restricted',
  emailDomain = 'email_domain',
  anyone = 'anyone',
}

export enum SharingErrorCodes {
  cookieExpired = 'cookieExpired',
  inactiveLink = 'inactiveLink',
  shareExpired = 'shareExpired',
}

export enum ShareSourceType {
  tour = 'tour',
  presentation = 'presentation',
}

export type ShareType = 'link';

export type BasicShare = {
  createTime: string;
  creator: BasicUser;
  entityId: number;
  id: number;
  isActive: boolean;
  isDeleted: boolean;
  lastAccessed: string | null;
  shareType: 'link';
  shareUrl: string;
  token: string;
  updateTime: string;
  numberOfAccesses: number;
  averageDuration: number;
  totalCompleted: number;
  folderId: string;
  workspaceId: string;
  filterEmailDomains?: boolean;
};

export type CommonEntityMetadata = {
  demoId: string;
  forClient: string;
};

export type DemoShare = BasicShare & {
  entityType: 'presentation';
  options: Omit<ShareLinkOptions, 'autoSyncUpdates'>;
  entityMetadata: {
    demoId: string;
    forClient: string;
    sharedFromDemo: boolean;
  };
};

export type TourShare = BasicShare & {
  entityType: 'tour';
  options: ShareLinkOptions;
};

export interface ShareLinkOptions {
  linkDescription: string;
  skipWelcome: boolean;
  security: ShareSecurity;
  expiresAt: number | null;
  daysToExpire: number | null;
  autoSyncUpdates?: boolean;
  tourAspectRatio?: TourAspectRatio;
  integrations?: IntegrationVendor[];
  welcomeMatConfig?: WelcomeMatConfig;
  showGuidanceTooltip?: boolean;
  welcomeMatTitle: string;
}

// TODO: deprecated.
//  use DemoShare or TourShare
//  clean usages and remove
export interface ShareLink {
  createTime: string;
  creator: BasicUser;
  entityId: number;
  entityType: ShareSourceType;
  id: number;
  isActive: boolean;
  isDeleted: boolean;
  options: ShareLinkOptions;
  lastAccessed: string;
  shareType: ShareType;
  shareUrl: string;
  token: string;
  updateTime: string;
  numberOfAccesses: number;
  averageDuration: number;
  totalCompleted: number;
  folderId: string;
  workspaceId: string;
  filterEmailDomains?: boolean;
}

export type SecuredShareCredentials = {
  organizationId: string;
  numericOrganizationId: string;
  token: string;
};
export type SecuredShareInitialRequestInput = SecuredShareCredentials & {
  shareOrigin: string;
};
export type SecuredSharePasscodeRequestInput = SecuredShareCredentials & {
  shareOrigin: string;
  userName?: string;
  passcode?: string;
  email?: string;
};
export type SecuredShareEmailRequestInput = SecuredShareCredentials & {
  userName: string;
  email: string;
};

export interface ShareMetadataResponse {
  creatorEmail: string;
  creatorName: string;
  emailDomain: string;
  expiresAt?: number;
  entityType: ShareSourceType;
  image: string;
  imageUrl: string;
  organizationLogo?: string;
  organizationName: string;
  organizationSlug: string;
  organizationId: string;
  securityLevel: ShareSecurityLevel;
  skipWelcome: boolean;
  integrations?: IntegrationVendor[];
  welcomeMatConfig?: WelcomeMatConfig;
}

export type SecuredShareMetadata = ShareMetadataResponse &
  SecuredShareCredentials;

export type SecuredShareEntityContent = {
  xShareSessionToken: string;
  exp: Date | string | number;
  creator: IUser;
  firstUrl?: string;
  forClient?: string;
  organizationId: string | number;
  organizationLogo: string;
  organizationName: string;
  organizationSlug: string;
  playerToken?: string;
  presentationId?: number | string;
  revisionId: number;
  shareId: number | string;
  skipWelcome: boolean;
  sourceType: ShareSourceType;
  frames?: TourFrame[];
  tour?: Tour;
  image?: string;
  tourAspectRatio?: TourAspectRatio;
};
export type SecuredShareErrorState = {
  errorTitle: string;
  errorMessage: string;
  errorCode: SharingErrorCodes;
  creatorEmail?: string;
  organizationName?: string;
};
export type securedShareErrorResponse = {
  errorState: SecuredShareErrorState;
};

export interface IPresenter {
  email?: string;
  name?: string;
}

export enum PresentationType {
  share = 'share',
  liveDemo = 'liveDemo',
}

export type SecuredShareInitialData = {
  presentationType: PresentationType;
  metadata?: SecuredShareMetadata;
  entityContent?: SecuredShareEntityContent & {
    name?: string;
    email?: string;
  };
  errorState?: SecuredShareErrorState;
  referrer?: string;
  ldKey?: string;
};

export type LiveDemoInitialData = {
  presentationType: PresentationType;
  metadata?: SecuredShareMetadata;
  entityContent?: SecuredShareEntityContent;
  errorState?: SecuredShareErrorState;
  activeUser?: IPresenter;
};
export type ShareAuthTokenData = {
  user_email: string;
  user_name: string;
  organization_id: string;
  share_id: string;
  exp: string;
};

export enum SourceTypeToText {
  tour = 'tour',
  presentation = 'demo',
}

export const tokenExpiredErrorMessage = 'Share Jwt Token Expired';
export const shareExpiredErrorMessage = 'Share Link Expired';

export type ShareSecurity = {
  level: ShareSecurityLevel;
  emailDomain?: string;
  emails?: string[];
  filterEmailDomains?: boolean;
};

export interface CreateShareQueryInput {
  sourceType: ShareSourceType;
  id: number;
  linkDescription: string;
  skipWelcome: boolean;
  expiresAt: number;
  daysToExpire: number;
  security: ShareSecurity;
  autoSyncUpdates?: boolean;
  tourAspectRatio?: TourAspectRatio;
}

export type TourAspectRatio = 'fixedWidthAndHeight' | 'responsive';

export interface EditShareQueryInput {
  expiresAt: number;
  daysToExpire: number;
  linkDescription: string;
  security: ShareSecurity;
  shareId: string | number;
  skipWelcome: boolean;
  autoSyncUpdates?: boolean;
  tourAspectRatio?: TourAspectRatio;
}

export interface WelcomeMatConfig {
  requireName: boolean;
  requireEmail: boolean;
}
